import { InventoryItemFragment } from '@epicuro-next/data/product';
import {
  getUTCNow,
  makeIntervalISODate,
  MAX_DATE,
} from '@epicuro-next/utilities';
import { InventoryItem } from 'graphql-api/gql-api-interfaces';
import { Interval } from 'luxon';

import { getQuantityNumber } from '../../../shared/get-quantity-number';

// The purpose of this function is to return an availability range
// It should start from today date or any future date
// If no result interval can be returned, it returns null
export const getAvailabilityRange = (inventories: InventoryItem[]) => {
  const unionInterval = inventories
    .filter((item) => getQuantityNumber(item.quantity) > 0)
    .map((item) =>
      makeIntervalISODate(item.availableFromDateUtc, item.availableToDateUtc),
    )
    .map((isoInterval) => Interval.fromISO(isoInterval, { zone: 'utc' }))
    .reduce<Interval | null>(
      (aggregated, current) =>
        aggregated ? aggregated.union(current) : current,
      null,
    );

  if (!unionInterval) {
    return null;
  }

  // intersect resultive availability interval with [now, infinity) interval
  const result = unionInterval.intersection(
    Interval.fromISO(makeIntervalISODate(getUTCNow(), MAX_DATE.toISOString())),
  );
  if (!result) {
    return null;
  }
  return {
    from: result.start.toJSDate(),
    to: result.end.toJSDate(),
  };
};

// The purpose of this function is to return an availability ranges
export const getAvailabilityRanges = (
  inventories: InventoryItem[] | InventoryItemFragment[],
) => {
  const availableIntervalsSet = new Set();
  const availableIntervals = inventories
    .filter((item) => getQuantityNumber(item.quantity) > 0)
    .map((item) =>
      makeIntervalISODate(item.availableFromDateUtc, item.availableToDateUtc),
    )
    .map((isoInterval) => Interval.fromISO(isoInterval, { zone: 'utc' }))
    // remove duplicates
    .filter((interval) => {
      if (availableIntervalsSet.has(interval.toISODate())) {
        return false;
      }

      availableIntervalsSet.add(interval.toISODate());
      return true;
    });

  // for performance implementation intervals could be optimized
  // for instance: [01-01-2021/01-04-2021, 01-02-2021/01-03-2021, 01-05-2021/01-06-2021, 10-05-2021/01-07-2021] ... etc
  // could be squashed to: [01-01-2021/01-04-2021, 01-05-2021/01-07-2021]
  const sortedAvailableVariants = availableIntervals
    .slice()
    .sort((a: Interval, b: Interval) =>
      a.toISODate() > b.toISODate() ? 1 : -1,
    );
  return _mergedIntersectingIntervals(sortedAvailableVariants);
};

/**
 * private API
 * @param intervals has to be sorted from lowest-highest starting date
 */
// tslint:disable-next-line:variable-name
export const _mergedIntersectingIntervals = (intervals: Interval[]) => {
  const intervalMap = new Map();

  let counter = 0;
  while (counter < intervals.length) {
    const curr = intervals[counter];
    const prev = [...intervalMap.values()].slice(-1).pop();

    if (prev && curr.overlaps(prev)) {
      intervalMap.delete(prev.toISODate());
      const union = curr.union(prev);
      intervalMap.set(union.toISODate(), union);
    } else {
      intervalMap.set(curr.toISODate(), curr);
    }

    counter += 1;
  }

  return [...intervalMap.values()];
};
